import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Apple, Smartphone, Link2, Share2, Compass, UserPlus, Check, Gift, Send, MessageSquare, Loader2 } from 'lucide-react';
import PhoneMockup from '../components/PhoneMockup';
import { features } from '../mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackEmail, setFeedbackEmail] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState(null);

  const handleSendFeedback = async () => {
    if (!feedbackMessage.trim()) return;
    setFeedbackSending(true);
    setFeedbackStatus(null);
    try {
      const res = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: feedbackMessage, email: feedbackEmail }),
      });
      if (!res.ok) throw new Error('Failed');
      setFeedbackStatus('success');
      setFeedbackMessage('');
      setFeedbackEmail('');
    } catch (e) {
      setFeedbackStatus('error');
    } finally {
      setFeedbackSending(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const logoUrl = "https://customer-assets.emergentagent.com/job_fltr-discovery/artifacts/eyyllr9s_zugf049p_%7B57BB1C3A-D64A-4745-A035-1D9438C12699%7D.png";

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-white">
      {/* Floating Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-lg z-50 border-b-4 border-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img src={logoUrl} alt="FltrLnk" className="h-12 w-auto" />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => scrollToSection('features')} className="text-gray-700 hover:text-blue-600 font-semibold transition-colors">
                Features
              </button>
              <Link to="/4individuals" className="text-gray-700 hover:text-blue-600 font-semibold transition-colors">
                4Individuals
              </Link>
              <Link to="/4business" className="text-gray-700 hover:text-blue-600 font-semibold transition-colors">
                4Business
              </Link>
              <Link to="/fltrmax" className="text-gray-700 hover:text-purple-600 font-semibold transition-colors">
                FltrMax
              </Link>
              <a href="https://apps.apple.com/app/id6755500827" target="_blank" rel="noopener noreferrer" className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-red-700 transition-all flex items-center gap-2">
                <Apple className="w-4 h-4" />
                Download Apple
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.gorkemyagci.ftrlnkmobileapp&pli=1" target="_blank" rel="noopener noreferrer" className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-green-700 transition-all flex items-center gap-2">
                <Smartphone className="w-4 h-4" />
                Download Android
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-gray-700 hover:text-blue-600 transition-colors"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden pb-4 space-y-2">
              <button onClick={() => scrollToSection('features')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 rounded-lg font-semibold">
                Features
              </button>
              <Link to="/4individuals" className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 rounded-lg font-semibold">
                4Individuals
              </Link>
              <Link to="/4business" className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 rounded-lg font-semibold">
                4Business
              </Link>
              <Link to="/fltrmax" className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-purple-50 rounded-lg font-semibold">
                FltrMax
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            {/* Content */}
            <div className="flex-1 text-center lg:text-left">
              <h2 className="text-5xl lg:text-7xl font-black text-gray-900 mb-6 leading-tight">
                Discover Your
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-800">
                  Next Connection
                </span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl">
                <span className="font-bold text-blue-600">FltrLnk</span> - The Free Next-Gen User Discovery Platform. 
                FltrLnk revolutionizes how you discover people, businesses, and opportunities around you. Search, Swipe, and Fltr to connect, explore, and grow within an extensive community to find your missing LNK.
              </p>
              
              {/* Download Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                <a href="https://apps.apple.com/app/id6755500827" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-red-600 text-white px-8 py-4 rounded-xl hover:bg-red-700 transition-all font-semibold shadow-xl">
                  <Apple className="w-6 h-6" />
                  Download on App Store
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.gorkemyagci.ftrlnkmobileapp&pli=1" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-green-600 text-white px-8 py-4 rounded-xl hover:bg-green-700 transition-all font-semibold shadow-xl">
                  <Smartphone className="w-6 h-6" />
                  Download on Google Play
                </a>
              </div>

              {/* CTA Button */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button 
                  onClick={() => scrollToSection('fltrlite-pro')}
                  className="bg-blue-600 text-white px-8 py-4 rounded-xl font-bold hover:bg-blue-700 transition-all shadow-xl"
                >
                  Learn More
                </button>
              </div>
            </div>

            {/* Phone Mockup */}
            <div className="flex-1 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full blur-3xl opacity-30 animate-pulse"></div>
                <PhoneMockup 
                  placeholder="Swipe to Discover" 
                  imageUrl="https://customer-assets.emergentagent.com/job_68d28ed4-bc93-4225-90c1-ea1c40af32dc/artifacts/ph4anwvv_IMG_2382.jpeg"
                  className="relative z-10 transform hover:scale-105 transition-transform" 
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Built For Businesses Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-6">
              Built For Users That Want Visibility and People Looking To Discover Them
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-10">
            {/* Restaurants & Food Trucks */}
            <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-blue-100 hover:border-blue-500 transition-colors">
              <div className="text-4xl mb-4">🍽️</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Restaurants & Food Trucks</h3>
              <p className="text-gray-600 text-sm">Get discovered by hungry customers nearby looking for their next great meal.</p>
            </div>

            {/* Service Businesses */}
            <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-blue-100 hover:border-blue-500 transition-colors">
              <div className="text-4xl mb-4">💼</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Service Businesses</h3>
              <p className="text-gray-600 text-sm">Beauty, Fitness, Trades, Professionals — connect with clients who need your expertise.</p>
            </div>

            {/* Experiences & Activities */}
            <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-blue-100 hover:border-blue-500 transition-colors">
              <div className="text-4xl mb-4">🎉</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Experiences & Activities</h3>
              <p className="text-gray-600 text-sm">Night Life, Local Attractions, and Adventures — help people find unforgettable experiences.</p>
            </div>

            {/* People */}
            <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-blue-100 hover:border-blue-500 transition-colors">
              <div className="text-4xl mb-4">👥</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">People</h3>
              <p className="text-gray-600 text-sm">Connect with like-minded individuals, make new friends, and expand your personal network.</p>
            </div>

            {/* Brands */}
            <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-blue-100 hover:border-blue-500 transition-colors">
              <div className="text-4xl mb-4">⭐</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Brands</h3>
              <p className="text-gray-600 text-sm">Build recognition and reach new audiences who align with your brand values.</p>
            </div>
          </div>

          <p className="text-center text-xl font-semibold text-blue-600">
            If people should be able to find your network faster, FltrLnk is built for you.
          </p>
        </div>
      </section>

      {/* Invite People You Know Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-green-50 via-white to-green-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-green-500 text-white px-6 py-2 rounded-full mb-6 text-sm font-bold">
              EARN REWARDS
            </div>
            <div className="flex items-center justify-center gap-3 mb-4">
              <Gift className="w-10 h-10 text-green-600" />
              <h2 className="text-4xl lg:text-5xl font-black text-gray-900">Invite People You Know!</h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Grow your network and earn User Boost Packs by inviting friends to FltrLnk
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-green-200">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Share Your FltrName</h3>
                  <p className="text-gray-700">Invite users to FltrLnk by sharing your unique FltrName as their invite code during profile creation.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Both Get Rewarded</h3>
                  <p className="text-gray-700">When an invited user uses your FltrName as their invite code, <span className="font-bold text-green-600">both you and the invited user</span> receive a User Boost Pack!</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Weekly Rewards for Active Users</h3>
                  <p className="text-gray-700">For every week that your invited user remains active on FltrLnk, <span className="font-bold text-green-600">both of you</span> will receive a User Boost Pack containing <span className="font-bold">1 boost for each of the 8 User Boosts</span>.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ways to Use Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4">
              Ways to Use FltrLnk
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Four powerful ways to engage with the FltrLnk community
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Connect */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl shadow-lg border-2 border-blue-200 hover:border-blue-500 transition-colors text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <UserPlus className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Connect</h3>
              <p className="text-gray-700">
                Build meaningful relationships through direct messaging and follow your favorite users to stay updated on their latest activity.
              </p>
            </div>

            {/* Access */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl shadow-lg border-2 border-green-200 hover:border-green-400 transition-colors text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Link2 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Access</h3>
              <p className="text-gray-700">
                Reach users directly through the app or access their social networks, websites, third-party links, email, and phone number if made public.
              </p>
            </div>

            {/* Share */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-8 rounded-2xl shadow-lg border-2 border-purple-200 hover:border-purple-400 transition-colors text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Share2 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Share</h3>
              <p className="text-gray-700">
                Showcase your best moments with users through your Top 20 photos and videos that highlight what makes you unique.
              </p>
            </div>

            {/* Discover */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-2xl shadow-lg border-2 border-orange-200 hover:border-orange-400 transition-colors text-center">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Compass className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Discover</h3>
              <p className="text-gray-700">
                Swipe, search, or Fltr to discover users around you or explore different places and find new connections everywhere.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black text-gray-900 mb-4">Innovative Features</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Innovating how users connect, discover, and expand networks
            </p>
          </div>

          {/* Display all 8 features with 1 phone each */}
          <div className="space-y-16">
            {features.map((feature, index) => (
              <div 
                key={feature.id} 
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}
              >
                {/* Content */}
                <div className="flex-1">
                  <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white w-12 h-12 rounded-xl flex items-center justify-center mb-4 text-xl font-bold">
                    {index + 1}
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-lg text-gray-600 mb-6">{feature.description}</p>
                </div>

                {/* Single Phone Screen */}
                <div className="flex-1 flex justify-center">
                  <PhoneMockup 
                    placeholder={feature.placeholder}
                    imageUrl={feature.imageUrl}
                    className="transform hover:scale-105 transition-transform"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Talk To Us Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900" data-testid="talk-to-us-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-white/10 text-white px-6 py-2 rounded-full mb-6 text-sm font-bold">
              <MessageSquare className="w-4 h-4" />
              TALK TO US
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-4">We'd Love To Hear From You</h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              We'd love to hear from our community of users. Share your insights on things we should add to the platform, or simply tell us your thoughts — your feedback helps shape the future of FltrLnk.
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8">
            {feedbackStatus === 'success' && (
              <div className="mb-6 p-4 rounded-xl bg-green-100 text-green-800 text-center font-semibold" data-testid="feedback-success">
                🎉 Thank you! Your feedback has been sent to our team.
              </div>
            )}
            {feedbackStatus === 'error' && (
              <div className="mb-6 p-4 rounded-xl bg-red-100 text-red-800 text-center font-semibold" data-testid="feedback-error">
                Something went wrong. Please try again in a moment.
              </div>
            )}

            <input
              type="email"
              value={feedbackEmail}
              onChange={(e) => setFeedbackEmail(e.target.value)}
              placeholder="Your email (optional)"
              data-testid="feedback-email-input"
              className="w-full mb-4 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-colors"
            />
            <textarea
              value={feedbackMessage}
              onChange={(e) => setFeedbackMessage(e.target.value)}
              placeholder="Type your feedback here..."
              rows={5}
              data-testid="feedback-message-input"
              className="w-full mb-4 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-colors resize-none"
            />
            <button
              onClick={handleSendFeedback}
              disabled={feedbackSending || !feedbackMessage.trim()}
              data-testid="feedback-send-button"
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
            >
              {feedbackSending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Send Feedback
                </>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* Earn User Boost Rewards Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-amber-50 to-orange-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-2 rounded-full mb-6 text-sm font-bold">
              REWARDS SYSTEM
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4">Earn User Boost Rewards</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Get rewarded with User Boosts just for using the platform!
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* One-Time Rewards */}
            <div className="bg-white p-6 rounded-2xl shadow-xl border-2 border-amber-200 hover:border-amber-400 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">One-Time Rewards</h3>
              </div>
              <p className="text-gray-700">
                Earn User Boosts for <span className="font-semibold text-amber-600">completing your profile setup</span> and using features for the first time.
              </p>
            </div>

            {/* Daily & Weekly Rewards */}
            <div className="bg-white p-6 rounded-2xl shadow-xl border-2 border-orange-200 hover:border-orange-400 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Daily & Weekly Boosts</h3>
              </div>
              <p className="text-gray-700">
                Earn <span className="font-semibold text-orange-600">guaranteed daily and weekly User Boosts</span> for using different features throughout the app.
              </p>
            </div>

            {/* Streak Rewards */}
            <div className="bg-white p-6 rounded-2xl shadow-xl border-2 border-red-200 hover:border-red-400 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Streak Rewards</h3>
              </div>
              <p className="text-gray-700">
                Build <span className="font-semibold text-red-600">swipe streaks</span> and <span className="font-semibold text-red-600">daily active user streaks</span> to earn bonus User Boosts.
              </p>
            </div>

            {/* Milestone Rewards */}
            <div className="bg-white p-6 rounded-2xl shadow-xl border-2 border-purple-200 hover:border-purple-400 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Milestone Rewards</h3>
              </div>
              <p className="text-gray-700">
                Complete <span className="font-semibold text-purple-600">user milestones</span> and unlock User Boosts as you reach new achievements.
              </p>
            </div>

            {/* Random Daily Rewards */}
            <div className="bg-white p-6 rounded-2xl shadow-xl border-2 border-blue-200 hover:border-blue-500 transition-colors md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Random Daily Rewards</h3>
              </div>
              <p className="text-gray-700">
                Earn User Boosts <span className="font-semibold text-blue-600">randomly each day</span> simply for using the app and interacting with other users. The more you engage, the more chances to earn!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Future of Things FltrLnk Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-2 rounded-full mb-6 text-sm font-bold">
              INNOVATION AHEAD
            </div>
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4">The Future of Things FltrLnk</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We look to keep innovating our platform as the FltrLnk ecosystem expands with innovations on how users can earn while using FltrLnk and expand entrepreneurial activities through content, communication, skillsets, and more.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Mentor User Profiles */}
            <div className="bg-white p-6 rounded-2xl shadow-xl border-2 border-indigo-200 hover:border-indigo-400 transition-colors">
              <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Mentor User Profiles</h3>
              <p className="text-gray-700 text-sm">
                User profiles that benefit users who have expertise in areas that others can learn from, allowing these users to take advantage of <span className="font-bold text-indigo-600">early access toward monetization</span> and future new features.
              </p>
            </div>

            {/* ROOMZ */}
            <div className="bg-white p-6 rounded-2xl shadow-xl border-2 border-purple-200 hover:border-purple-400 transition-colors">
              <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">ROOMZ</h3>
              <p className="text-gray-700 text-sm">
                Private discord chat rooms users can have for their followers where they can get more personal with user engagement. Great for <span className="font-bold text-purple-600">entrepreneurs</span> who look to share knowledge, groups who look to have a more private interaction, or all users who look to engage more personally with their audience.
              </p>
            </div>

            {/* Earn Your SOCIAL Leisure */}
            <div className="bg-white p-6 rounded-2xl shadow-xl border-2 border-emerald-200 hover:border-emerald-400 transition-colors">
              <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Earn Your SOCIAL Leisure</h3>
              <p className="text-gray-700 text-sm">
                Users will be able to monetize on the platform with 4 initial monetization gateways: <span className="font-bold text-emerald-600">Preferred Chats, Hidden Content, Key Pass, and Subscriptions</span>. Earn <span className="font-bold text-emerald-600">95:5 splits</span> from monetization, set rates at a one time, weekly, or monthly rate. Must be subscribed to FltrLite to monetize.
              </p>
            </div>

            {/* Preferred Chats */}
            <div className="bg-white p-6 rounded-2xl shadow-xl border-2 border-blue-200 hover:border-blue-500 transition-colors">
              <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Preferred Chats</h3>
              <p className="text-gray-700 text-sm">
                Earn from chats sent to you from other users who you do not follow. <span className="font-bold text-blue-600">Monetize your attention</span> and make meaningful connections while getting compensated for your time and engagement.
              </p>
            </div>

            {/* Hidden Content */}
            <div className="bg-white p-6 rounded-2xl shadow-xl border-2 border-pink-200 hover:border-pink-400 transition-colors">
              <div className="w-14 h-14 bg-pink-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Hidden Content</h3>
              <p className="text-gray-700 text-sm">
                Earn from hidden content posted to your profile from users you don't follow or even from users who are LNKD with you. <span className="font-bold text-pink-600">All content must follow FltrLnk guidelines</span>.
              </p>
            </div>

            {/* Key Pass */}
            <div className="bg-white p-6 rounded-2xl shadow-xl border-2 border-amber-200 hover:border-amber-400 transition-colors">
              <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Key Pass</h3>
              <p className="text-gray-700 text-sm">
                Allow users to have access to your <span className="font-bold text-amber-600">ROOMZ discord chat</span>. Grant exclusive entry to your private community space and build deeper connections with your most engaged followers.
              </p>
            </div>

            {/* Subscriptions */}
            <div className="bg-white p-6 rounded-2xl shadow-xl border-2 border-rose-200 hover:border-rose-400 transition-colors">
              <div className="w-14 h-14 bg-rose-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Subscriptions</h3>
              <p className="text-gray-700 text-sm">
                Earn from other users subscribing to your profile, allowing them to have access to chat you as well as <span className="font-bold text-rose-600">all hidden content and your ROOMZ</span>. Very key for users who have their profiles set to private.
              </p>
            </div>

            {/* Much More */}
            <div className="bg-white p-6 rounded-2xl shadow-xl border-2 border-violet-200 hover:border-violet-400 transition-colors">
              <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Much More</h3>
              <p className="text-gray-700 text-sm">
                We plan to <span className="font-bold text-violet-600">open the flood gates</span> on ways users can monetize in a multitude of ways across social platforms — this is just the beginning of what's possible on FltrLnk.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="mb-4">
                <h2 className="text-4xl font-black text-white">FltrLnk</h2>
              </div>
              <p className="text-gray-400 text-sm">
                The Next-Gen User Discovery Platform
              </p>
            </div>

            {/* FltrLnk */}
            <div>
              <h4 className="font-bold mb-4">FltrLnk</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('features')} className="text-gray-400 hover:text-white transition-colors">Features</button></li>
                <li><Link to="/4individuals" className="text-gray-400 hover:text-white transition-colors">4Individuals</Link></li>
                <li><Link to="/4business" className="text-gray-400 hover:text-white transition-colors">4Business</Link></li>
                <li><Link to="/fltrmax" className="text-gray-400 hover:text-white transition-colors">FltrMax</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-bold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/cookies" className="text-gray-400 hover:text-white transition-colors">Cookies Policy</Link></li>
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h4 className="font-bold mb-4">Connect</h4>
              <div className="flex gap-4 mb-4">
                <a href="https://instagram.com/fltrlnk" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center hover:from-purple-700 hover:to-pink-700 transition-colors">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a href="https://www.tiktok.com/@fltrlnk?_t=ZP-90nKJ7axl8g&_r=1" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-black rounded-lg flex items-center justify-center hover:bg-gray-800 transition-colors">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
                <a href="https://www.linkedin.com/company/fltrlnk" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center hover:bg-blue-800 transition-colors">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
              <div>
                <Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm">Contact Us</Link>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              © 2025 FltrLnk Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
